import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Trophy, Medal, Target, Shield, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import Footer from '../components/Footer';
import { Header } from '../components/Header';
import Seo from '../components/Seo';
import { leaderboardAPI } from '../services/api';

const TABS = [
  { key: 'coaches', label: 'Top Coaches', icon: Trophy },
  { key: 'goals', label: 'Top Scorers', icon: Target },
  { key: 'assists', label: 'Assists', icon: Zap },
  { key: 'rating', label: 'Best Rating', icon: Medal },
  { key: 'defense', label: 'Best Defense', icon: Shield },
];

const PAGE_SIZE = 25;

const Leaderboard = () => {
  const [tab, setTab] = useState('coaches');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    const fetcher =
      tab === 'coaches' ? leaderboardAPI.getGlobalLeaderboard :
      tab === 'goals' ? leaderboardAPI.getGoalsLeaderboard :
      tab === 'assists' ? leaderboardAPI.getAssistsLeaderboard :
      tab === 'rating' ? leaderboardAPI.getRatingLeaderboard :
      leaderboardAPI.getDefenseLeaderboard;

    fetcher(params)
      .then((res) => {
        const d = res.data;
        setData(d.leaderboard || d.data || d.rows || []);
        setTotal(d.pagination?.total || d.total || 0);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [tab, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Global Leaderboard | My Football Game',
    description: 'See the top coaches, goal scorers, assist leaders and highest-rated players in My Football Game.',
    url: 'https://www.myfootballgame.com/leaderboard',
    isPartOf: { '@type': 'WebSite', name: 'My Football Game', url: 'https://www.myfootballgame.com' },
  };

  return (
    <motion.div
      className="w-full min-h-screen overflow-hidden"
      style={{ background: 'var(--surface-0)', color: 'var(--text-1)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <Seo
        title="Global Leaderboard - Top Coaches & Players | My Football Game"
        description="Check the live global leaderboard: top coaches by wins, goal scorers, assist leaders, best-rated players and tightest defences in My Football Game."
        path="/leaderboard"
        jsonLd={jsonLd}
      />

      <div className="container mx-auto px-4">
        <Header />
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-16">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="text-center mb-8"
        >
          <div className="pc-eyebrow pc-eyebrow-accent mb-2">Rankings</div>
          <h1 className="pc-display text-4xl md:text-5xl mb-3">
            GLOBAL<span style={{ color: 'var(--accent)' }}>.</span>LEADERBOARD
          </h1>
          <p className="text-sm text-[var(--text-3)] max-w-xl mx-auto">
            The best coaches and players in My Football Game, ranked by performance across all competitive matches.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 justify-center flex-wrap">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setPage(0); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all"
                style={{
                  background: active ? 'var(--accent)' : 'var(--surface-1)',
                  color: active ? 'var(--surface-0)' : 'var(--text-2)',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border-edge)'}`,
                }}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-16 text-[var(--text-3)]">No data yet — be the first to climb the ranks!</div>
          ) : (
            <>
              {/* Header */}
              <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[var(--text-3)]" style={{ borderBottom: '1px solid var(--border-edge)' }}>
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-5">{tab === 'coaches' ? 'Coach / Team' : 'Player / Team'}</div>
                {tab === 'coaches' && <><div className="col-span-2 text-center">Record</div><div className="col-span-2 text-center">Score</div><div className="col-span-2 text-center">Win Rate</div></>}
                {tab === 'goals' && <><div className="col-span-2 text-center">Goals</div><div className="col-span-2 text-center">Matches</div><div className="col-span-2 text-center">Per Match</div></>}
                {tab === 'assists' && <><div className="col-span-2 text-center">Assists</div><div className="col-span-2 text-center">Matches</div><div className="col-span-2 text-center">Per Match</div></>}
                {tab === 'rating' && <><div className="col-span-2 text-center">Rating</div><div className="col-span-2 text-center">Matches</div><div className="col-span-2 text-center">Position</div></>}
                {tab === 'defense' && <><div className="col-span-2 text-center">Clean Sheets</div><div className="col-span-2 text-center">Goals Against</div><div className="col-span-2 text-center">Matches</div></>}
              </div>

              {/* Rows */}
              {data.map((item, i) => {
                const rank = page * PAGE_SIZE + i + 1;
                const isTop3 = rank <= 3;
                const rankColors = ['#f59e0b', '#9ca3af', '#cd7f32'];
                return (
                  <motion.div
                    key={i}
                    className="grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm transition-colors hover:bg-[var(--surface-2)]"
                    style={{ borderBottom: '1px solid var(--border-edge)' }}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.02 }}
                  >
                    <div className="col-span-1 text-center">
                      {isTop3 ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold" style={{ background: `${rankColors[rank - 1]}20`, color: rankColors[rank - 1] }}>
                          {rank}
                        </span>
                      ) : (
                        <span className="text-[var(--text-3)] font-bold text-xs">{rank}</span>
                      )}
                    </div>
                    <div className="col-span-5">
                      <div className="font-bold text-sm" style={{ color: isTop3 ? 'var(--accent)' : 'var(--text-1)' }}>
                        {item.coach_name || item.player_name || item.name || '—'}
                      </div>
                      <div className="text-[10px] text-[var(--text-3)]">
                        {item.team_name || ''}
                      </div>
                    </div>

                    {tab === 'coaches' && (
                      <>
                        <div className="col-span-2 text-center text-xs">
                          <span style={{ color: 'var(--win)' }}>{item.wins || 0}W</span>{' '}
                          <span style={{ color: 'var(--draw)' }}>{item.draws || 0}D</span>{' '}
                          <span style={{ color: 'var(--loss)' }}>{item.losses || 0}L</span>
                        </div>
                        <div className="col-span-2 text-center font-bold" style={{ color: 'var(--accent)' }}>
                          {item.score ?? item.total_score ?? 0}
                        </div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">
                          {item.wins && (item.wins + (item.draws || 0) + (item.losses || 0)) > 0
                            ? `${Math.round((item.wins / (item.wins + (item.draws || 0) + (item.losses || 0))) * 100)}%`
                            : '—'}
                        </div>
                      </>
                    )}
                    {tab === 'goals' && (
                      <>
                        <div className="col-span-2 text-center font-bold" style={{ color: 'var(--accent)' }}>{item.goals ?? item.total_goals ?? 0}</div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">{item.matches ?? item.matches_played ?? 0}</div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">
                          {(item.matches ?? item.matches_played) > 0 ? ((item.goals ?? item.total_goals ?? 0) / (item.matches ?? item.matches_played)).toFixed(2) : '—'}
                        </div>
                      </>
                    )}
                    {tab === 'assists' && (
                      <>
                        <div className="col-span-2 text-center font-bold" style={{ color: 'var(--accent)' }}>{item.assists ?? item.total_assists ?? 0}</div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">{item.matches ?? item.matches_played ?? 0}</div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">
                          {(item.matches ?? item.matches_played) > 0 ? ((item.assists ?? item.total_assists ?? 0) / (item.matches ?? item.matches_played)).toFixed(2) : '—'}
                        </div>
                      </>
                    )}
                    {tab === 'rating' && (
                      <>
                        <div className="col-span-2 text-center font-bold" style={{ color: 'var(--accent)' }}>{(item.avg_rating ?? item.rating ?? 0).toFixed ? (item.avg_rating ?? item.rating ?? 0).toFixed(1) : item.avg_rating ?? item.rating ?? 0}</div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">{item.matches ?? item.matches_played ?? 0}</div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">{item.position || item.detailed_position || '—'}</div>
                      </>
                    )}
                    {tab === 'defense' && (
                      <>
                        <div className="col-span-2 text-center font-bold" style={{ color: 'var(--accent)' }}>{item.clean_sheets ?? 0}</div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">{item.goals_against ?? item.goals_conceded ?? 0}</div>
                        <div className="col-span-2 text-center text-xs text-[var(--text-2)]">{item.matches ?? item.matches_played ?? 0}</div>
                      </>
                    )}
                  </motion.div>
                );
              })}
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-2 rounded text-xs font-bold disabled:opacity-30 transition-all"
              style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)', color: 'var(--text-2)' }}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-[var(--text-3)]">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-2 rounded text-xs font-bold disabled:opacity-30 transition-all"
              style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)', color: 'var(--text-2)' }}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 rounded-xl p-8 text-center relative overflow-hidden"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
        >
          <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 0%, var(--accent), transparent 60%)' }} />
          <h2 className="text-2xl font-bold mb-3 relative z-10">Think you can make the top 10?</h2>
          <p className="text-sm text-[var(--text-2)] mb-6 relative z-10">
            Create your club for free, build your squad, and start climbing the rankings today.
          </p>
          <Link to="/register">
            <motion.button
              className="px-6 py-3 rounded font-bold text-[13px] uppercase tracking-[0.2em] transition-all relative z-10"
              style={{ background: 'var(--accent)', color: 'var(--surface-0)', boxShadow: '0 0 24px var(--accent-glow)' }}
              whileHover={{ y: -2 }}
              whileTap={{ y: 0 }}
            >
              Start Playing Free
            </motion.button>
          </Link>
        </motion.div>
      </div>

      <Footer />
    </motion.div>
  );
};

export default Leaderboard;
